import { useEffect, useState, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldAlert, Plus, Pencil, Trash2, Check, ArrowRightLeft, ArrowDownToLine, Search } from "lucide-react";

const ADJUSTMENT_TYPES = [
  { value: "positive_adjustment", label: "Positive adjustment" },
  { value: "negative_adjustment", label: "Negative adjustment" },
  { value: "late_payroll_amendment", label: "Late payroll amendment" },
  { value: "new_starter_adjustment", label: "New starter adjustment" },
  { value: "leaver_adjustment", label: "Leaver adjustment" },
  { value: "manual_correction", label: "Manual correction" },
];

const STATUS_TONE = {
  pending: "text-amber-700 border-amber-200 bg-amber-50",
  included_next_month: "text-blue-700 border-blue-200 bg-blue-50",
  partially_settled: "text-purple-700 border-purple-200 bg-purple-50",
  settled: "text-emerald-700 border-emerald-200 bg-emerald-50",
  manually_closed: "text-slate-700 border-slate-300 bg-slate-50",
};

const STATUS_LABEL = {
  pending: "Pending",
  included_next_month: "Included next month",
  partially_settled: "Partially settled",
  settled: "Settled",
  manually_closed: "Manually closed",
};

function emptyForm(employees) {
  return {
    employee_id: employees[0]?.id || "",
    source_month: "",
    expected_settlement_month: "",
    adjustment_amount: "",
    adjustment_type: "late_payroll_amendment",
    reason: "",
  };
}

export default function PensionAdjustmentsPage() {
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm([]));

  const load = async () => {
    setLoading(true);
    try {
      const [list, sum, emps] = await Promise.all([
        api.get("/pension-adjustments"),
        api.get("/pension-adjustments/summary"),
        api.get("/employees?status=active"),
      ]);
      setItems(list.data);
      setSummary(sum.data);
      setEmployees(emps.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => items.filter((r) => {
    if (statusFilter !== "all" && r.status !== statusFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      if (!(r.employee_name || "").toLowerCase().includes(s)
        && !(r.employee_payroll_id || "").toLowerCase().includes(s)
        && !(r.reason || "").toLowerCase().includes(s)) return false;
    }
    return true;
  }), [items, search, statusFilter]);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm(employees));
    setDialogOpen(true);
  };
  const openEdit = (adj) => {
    setEditing(adj);
    setForm({
      ...adj,
      adjustment_amount: String(adj.adjustment_amount),
    });
    setDialogOpen(true);
  };

  const submit = async () => {
    const payload = {
      employee_id: form.employee_id,
      source_month: form.source_month,
      expected_settlement_month: form.expected_settlement_month,
      adjustment_amount: Number(form.adjustment_amount),
      adjustment_type: form.adjustment_type,
      reason: form.reason,
    };
    try {
      if (editing) {
        await api.patch(`/pension-adjustments/${editing.id}`, payload);
        toast.success("Adjustment updated");
      } else {
        await api.post("/pension-adjustments", payload);
        toast.success("Adjustment created");
      }
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const markIncluded = async (adj) => {
    try {
      await api.post(`/pension-adjustments/${adj.id}/mark-included`);
      toast.success("Marked as included next month");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const settle = async (adj, partial = false) => {
    try {
      const params = partial ? { partial: true } : {};
      await api.post(`/pension-adjustments/${adj.id}/settle`, null, { params });
      toast.success(partial ? "Marked as partially settled" : "Settled");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (adj) => {
    if (!window.confirm(`Delete adjustment €${adj.adjustment_amount} for ${adj.employee_name}?`)) return;
    try {
      await api.delete(`/pension-adjustments/${adj.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const byStatus = summary?.by_status || {};

  return (
    <div className="space-y-6" data-testid="pension-adjustments-page">
      <div>
        <div className="tiny-label">Phase 3 · Zurich carry-forward · admin only</div>
        <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Pension adjustments ledger</h1>
        <p className="text-muted-foreground mt-1 max-w-3xl">
          Zurich does not adjust mid-month. Use this ledger when payroll is amended <em>after</em> the Zurich auto-debit for a month has already been processed.
          Record the variance against the original month and mark when it's expected to settle. The Zurich reconciliation engine will automatically apply pending/included adjustments to the right month.
        </p>
      </div>

      <Alert className="border-blue-200 bg-blue-50 text-blue-800">
        <ShieldAlert className="w-4 h-4" />
        <AlertTitle>Lifecycle</AlertTitle>
        <AlertDescription>
          <span className="font-semibold">pending</span> → <span className="font-semibold">included_next_month</span> → <span className="font-semibold">settled</span> (or <span className="font-semibold">partially_settled</span> / <span className="font-semibold">manually_closed</span>). Reconciliation only uses adjustments not yet fully settled.
        </AlertDescription>
      </Alert>

      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {["pending", "included_next_month", "partially_settled", "settled", "manually_closed"].map((s) => (
          <Card key={s} className="border-border shadow-none">
            <CardContent className="p-5">
              <div className="tiny-label">{STATUS_LABEL[s]}</div>
              <div className="font-display text-3xl font-bold mt-2" data-testid={`adj-summary-${s}`}>{byStatus[s]?.count ?? 0}</div>
              <div className="text-xs text-muted-foreground mt-1">€{(byStatus[s]?.total_amount ?? 0).toFixed(2)}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="border-border shadow-none">
        <CardContent className="p-4 grid sm:grid-cols-12 gap-3 items-end">
          <div className="sm:col-span-5">
            <Label>Search</Label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Employee, payroll id or reason" data-testid="adj-search" />
            </div>
          </div>
          <div className="sm:col-span-4">
            <Label>Status</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="adj-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {Object.entries(STATUS_LABEL).map(([k, label]) => (
                  <SelectItem key={k} value={k}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-3">
            <Button className="w-full h-10" onClick={openNew} data-testid="adj-add-btn">
              <Plus className="w-4 h-4 mr-1" /> Record adjustment
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border shadow-none">
        <div className="overflow-x-auto app-scroll">
          <table className="w-full text-sm" data-testid="adj-table">
            <thead className="bg-muted/50">
              <tr className="text-left tiny-label">
                <th className="px-3 py-3">Employee</th>
                <th className="px-3 py-3">Type</th>
                <th className="px-3 py-3 text-right">Amount</th>
                <th className="px-3 py-3">Source month</th>
                <th className="px-3 py-3">Settles in</th>
                <th className="px-3 py-3">Reason</th>
                <th className="px-3 py-3">Status</th>
                <th className="px-3 py-3">Settled at</th>
                <th className="px-3 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((adj) => (
                <tr key={adj.id} className="border-t border-border align-top" data-testid={`adj-row-${adj.id}`}>
                  <td className="px-3 py-3">
                    <div className="font-medium">{adj.employee_name}</div>
                    <div className="text-xs text-muted-foreground">{adj.employee_payroll_id || "—"}</div>
                  </td>
                  <td className="px-3 py-3 capitalize text-xs">{adj.adjustment_type.replace(/_/g, " ")}</td>
                  <td className={`px-3 py-3 text-right font-mono font-semibold ${adj.adjustment_amount < 0 ? "text-red-700" : "text-emerald-700"}`}>
                    €{adj.adjustment_amount.toFixed(2)}
                  </td>
                  <td className="px-3 py-3 font-mono">{adj.source_month}</td>
                  <td className="px-3 py-3 font-mono">{adj.expected_settlement_month}</td>
                  <td className="px-3 py-3 text-xs max-w-[260px]">{adj.reason}</td>
                  <td className="px-3 py-3"><Badge variant="outline" className={STATUS_TONE[adj.status]}>{STATUS_LABEL[adj.status]}</Badge></td>
                  <td className="px-3 py-3 text-xs font-mono">{adj.settled_at ? new Date(adj.settled_at).toLocaleString() : "—"}</td>
                  <td className="px-3 py-3 text-right">
                    <div className="inline-flex gap-1">
                      {adj.status === "pending" && (
                        <Button size="sm" variant="outline" onClick={() => markIncluded(adj)} title="Mark as included next month" data-testid={`adj-include-${adj.id}`}>
                          <ArrowRightLeft className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {(adj.status === "pending" || adj.status === "included_next_month" || adj.status === "partially_settled") && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => settle(adj, true)} title="Mark as partially settled" data-testid={`adj-partial-${adj.id}`}>
                            <ArrowDownToLine className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => settle(adj, false)} title="Mark as settled" data-testid={`adj-settle-${adj.id}`}>
                            <Check className="w-3.5 h-3.5" />
                          </Button>
                        </>
                      )}
                      <Button size="sm" variant="outline" onClick={() => openEdit(adj)} data-testid={`adj-edit-${adj.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => remove(adj)} data-testid={`adj-delete-${adj.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-muted-foreground">
                  {loading ? "Loading…" : "No adjustments match the current filter."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="adj-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit adjustment" : "Record pension adjustment"}</DialogTitle>
            <DialogDescription>Carry-forward variance recorded against the original month, settling in a later Zurich debit.</DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Employee</Label>
              <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })} disabled={!!editing}>
                <SelectTrigger data-testid="adj-employee"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {employees.map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.full_name}{e.employee_id ? ` · ${e.employee_id}` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Type</Label>
              <Select value={form.adjustment_type} onValueChange={(v) => setForm({ ...form, adjustment_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ADJUSTMENT_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Source month (YYYY-MM)</Label>
              <Input value={form.source_month} onChange={(e) => setForm({ ...form, source_month: e.target.value })} placeholder="2025-11" data-testid="adj-source-month" />
            </div>
            <div>
              <Label>Expected settlement month (YYYY-MM)</Label>
              <Input value={form.expected_settlement_month} onChange={(e) => setForm({ ...form, expected_settlement_month: e.target.value })} placeholder="2025-12" data-testid="adj-settle-month" />
            </div>
            <div>
              <Label>Amount (€) — negative for refunds</Label>
              <Input type="number" step="0.01" value={form.adjustment_amount} onChange={(e) => setForm({ ...form, adjustment_amount: e.target.value })} data-testid="adj-amount" />
            </div>
            <div></div>
            <div className="sm:col-span-2">
              <Label>Reason</Label>
              <Textarea rows={3} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="e.g. Backdated November bonus added after payroll closed" data-testid="adj-reason" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={submit}
              disabled={!form.employee_id || !form.source_month || !form.expected_settlement_month || !form.adjustment_amount || !form.reason}
              data-testid="adj-submit">
              {editing ? "Save changes" : "Create adjustment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
